import { InvalidOperationException } from "../../../../Global/Base/Exceptions";
import { ISchemaValidationError, ISchemaValidationWarning, SchemaValidationErrorType, SchemaValidationWarningType } from "../../../../Global/Base/Utils/ObjectSchemaValidator";
import { StringUtils } from "../../../../Global/Base/Utils/StringUtils";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { IContentBuilder } from "./IContentBuilder";
/**
 * Builder pro zobrazení chyb a varování z validace vkládání uživatelské definice filtru
 */
export class FilterValidationMessageBuilder implements IContentBuilder {
    private _element: HTMLElement = createElement();
    private _errors: ISchemaValidationError<object>[];
    private _warnings: ISchemaValidationWarning[];

    private _errorLocKeys: Map<SchemaValidationErrorType, string> = new Map([
        [SchemaValidationErrorType.invalidDataType, "GUI-674369"], // Pole '@0' s hodnotou '@1' nemá správný datový typ
        [SchemaValidationErrorType.missingField, "GUI-674368"], // V definici filtru chybí pole '@0'
        [SchemaValidationErrorType.notAllowedValue, "GUI-674370"], // Hodnota '@1' v poli '@0' není mezi povolenými hodnotami
    ]);

    private _warningLocKeys: Map<SchemaValidationWarningType, string> = new Map([
        [SchemaValidationWarningType.unsupportedField, "GUI-674371"], // Pole '@0' není podporováno
    ]);

    private _errorLocalizations: Map<SchemaValidationErrorType, string> = new Map();

    private _warningLocalizations: Map<SchemaValidationWarningType, string> = new Map();

    constructor(errors: ISchemaValidationError<object>[], warnings: ISchemaValidationWarning[]) {
        this._errors = errors;
        this._warnings = warnings;
    }

    private async _getErrorLocalization(errorType: SchemaValidationErrorType): Promise<string> {
        if (this._errorLocalizations.has(errorType))
            return this._errorLocalizations.get(errorType);

        const errorLocKey = this._errorLocKeys.get(errorType);

        if (!errorLocKey)
            throw new InvalidOperationException(`[FilterValidationMessageBuilder] Missing locKey for errorType 'SchemaValidationErrorType.${SchemaValidationErrorType[errorType]}'!`); //`

        const errorLocalization = await Localization.getTextPromise(errorLocKey);

        this._errorLocalizations.set(errorType, errorLocalization);

        return errorLocalization;
    }

    private async _getWarningLocalization(warningType: SchemaValidationWarningType): Promise<string> {
        if (this._warningLocalizations.has(warningType))
            return this._warningLocalizations.get(warningType);

        const warningLocKey = this._warningLocKeys.get(warningType);

        if (!warningLocKey)
            throw new InvalidOperationException(`[FilterValidationMessageBuilder] Missing locKey for warningType 'SchemaValidationWarningType.${SchemaValidationWarningType[warningType]}'!`); //`

        const warningLocalization = await Localization.getTextPromise(warningLocKey);

        this._warningLocalizations.set(warningType, warningLocalization);

        return warningLocalization;
    }

    public async build(): Promise<HTMLElement> {
        if (this._errors && this._errors.length > 0) {
            const errorsElement = createElement();

            const errorTitle = createElement({ type: "p" });
            Localization.addLocData(errorTitle, "GUI-674372"); // Chyby:
            errorsElement.appendChild(errorTitle);

            const listElement = createElement({ type: "ul" });

            for (const validationError of this._errors) {
                const errorMessageTemplate: string = await this._getErrorLocalization(validationError.errorType);
                let errorMessage: string;

                switch (validationError.errorType) {
                    case SchemaValidationErrorType.missingField:
                        errorMessage = StringUtils.format(errorMessageTemplate, [validationError.field.name]);
                        break;
                    case SchemaValidationErrorType.invalidDataType:
                    case SchemaValidationErrorType.notAllowedValue:
                        errorMessage = StringUtils.format(errorMessageTemplate, [validationError.field.name, validationError.value]);
                        break;

                    default:
                }

                if (errorMessage) {
                    const listItemElement = createElement({ type: "li" });

                    listItemElement.appendChild(createElement({
                        type: "span",
                        //value: `Field '${validationError.field.name}' with value = '${validationError.value}' ended up with error '${SchemaValidationErrorType[validationError.errorType]}'.`
                        value: `${errorMessage}`,
                    })); // `
                    listElement.appendChild(listItemElement);
                }
            }

            errorsElement.appendChild(listElement);

            this._element.appendChild(errorsElement);
        }

        if (this._warnings && this._warnings.length > 0) {
            const warningsElement = createElement();

            const warningTitle = createElement({ type: "p" });
            Localization.addLocData(warningTitle, "GUI-674378"); // Varování:
            warningsElement.appendChild(warningTitle);

            const listElement = createElement({ type: "ul" });

            for (const validationWarning of this._warnings) {
                const warningMessageTemplate: string = await this._getWarningLocalization(validationWarning.warningType);
                let warningMessage: string;

                switch (validationWarning.warningType) {
                    case SchemaValidationWarningType.unsupportedField:
                        warningMessage = StringUtils.format(warningMessageTemplate, [validationWarning.fieldName]);
                        break;

                    default:
                }

                if (warningMessage) {
                    const listItemElement = createElement({ type: "li" });

                    listItemElement.appendChild(createElement({
                        type: "span",
                        //value: `Field '${validationWaring.fieldName && validationWaring.fieldName.toString()}' with value = '${validationWaring.value}' ended up with warning '${SchemaValidationWarningType[validationWaring.warningType]}'.`
                        value: `${warningMessage}`,
                    })); // `
                    listElement.appendChild(listItemElement);
                }
            }

            warningsElement.appendChild(listElement);

            this._element.appendChild(warningsElement);
        }

        const infoMessage = createElement();
        if (this._errors.length > 0)
            Localization.addLocData(infoMessage, "GUI-674375"); // Filtr nebyl aplikován!
        else
            Localization.addLocData(infoMessage, "GUI-674377"); // Filtr byl aplikován.
        this._element.appendChild(infoMessage);

        return await this._element;
    }
}
